import { ChangeEvent, FocusEvent, useEffect, KeyboardEvent } from 'react';
import { memo, useRef, useState } from 'react';
import { useFormikContext, useField } from 'formik';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocale } from '../../../../context/Localization/localization';
import { WebFormEmailFieldProps } from '../FormEmailField.types';
import { EmailPatternMask } from '../EmailPatternMask/EmailPatternMask';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';
import { GoMotoFunction } from '../../../../API/GoMoto.types';
import { useSuppressAutofillStyle } from '../../../../helpers/UseSupressAutofillStyle/useSuppressAutofillStyle';

export const WebFormEmailField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        initiallyMasked,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        tabFocusRefContext,
        onChangeCallback,
        noValidation,
    }: WebFormEmailFieldProps) => {
        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);

        const [field, meta] = useField<string>({
            name: name,
            validate: noValidation ? undefined : TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: 'email',
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();
        const { dealer: { primaryLanguage } } = useKioskState();
        const { inputClassName } = useSuppressAutofillStyle({
            inputRef: inputRef,
        });
        const locale = useLocale();
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        /**
         * Used to ensure that the mask re-runs when the field is focused or blurred
         * so that it correctly unmasks the email.
         */
        const [isFocused, setIsFocused] = useState<boolean>(false);
        /**
         * Intended to ensure the masking is handled correctly by ensuring a re-render.
         * May not be needed.
         */
        const [value, setValue] = useState<string>(field.value ?? '');
        /**
         * Boolean state to update the email pattern mask once the field has been focused
         * so that it never remasks the value.
         */
        const [wasUnmasked, setWasUnmasked] = useState<boolean>(false);

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Updates the value when formik's value changes externally.
             */
            if (field.value !== value) {
                setValue(field.value ?? '');
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [field.value]);

        useEffect(() => {
            const inputElement: HTMLInputElement | null = inputRef.current;

            const handlePasteEvent: GoMotoFunction<[ClipboardEvent], void> = (event) => {
                if (event.clipboardData && (/\s/).test(event.clipboardData.getData('text'))) {
                    event.preventDefault();
                }
            };

            const handleInputEvent: GoMotoFunction<[Event], void> = (_event: Event) => {
                if (inputRef.current && (/\s/).test(inputRef.current.value)) {
                    inputRef.current.value = '';
                    setValue('');
                    setFieldValue(name, '');
                }
            };

            if (inputElement) {
                // Listen for paste events and prevent any that contain spaces
                inputElement.addEventListener('paste', handlePasteEvent);

                /**
                 * Listen to all input events and set the current field to an empty string if the field gets a value with a space.
                 * This also works at preventing form autofills.
                 */
                inputElement.addEventListener('input', handleInputEvent);
            }

            return () => {
                if (inputElement) {
                    inputElement.removeEventListener('paste', handlePasteEvent);
                    inputElement.removeEventListener('input', handleInputEvent);
                }
            };
        }, [name, setFieldValue]);

        const handleOnBlur = () => {
            setIsFocused(false);
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            onChangeCallback && onChangeCallback();
            const value = event.target.value
                .replace(/[\u2018\u2019\u201A]/g, '\'')
                .replace(/[\u201C\u201D\u201E]/g, '"')
                .replace(/\u2026/g, '...')
                .replace(/[\u2013\u2014]/g, '-');
            setValue(value);
            setFieldValue(name, value);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event.target.value.indexOf('*') !== -1) {
                // Handles unmasking if the value was masked.
                setValue('');
            }
            if (!wasUnmasked) {
                // Handles preventing the field from remasking after the field has been focused once.
                setWasUnmasked(true);
            }
            setIsFocused(true);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'email'}
                type={'email'}
                inputRef={inputRef}
                value={value}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                InputProps={{
                    inputComponent: EmailPatternMask as any,
                }}
                inputProps={{
                    isMasked: initiallyMasked && !isFocused && !wasUnmasked,
                    className: inputClassName,
                }}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === ' ') {
                        e.preventDefault();
                    }
                }}
            />
        );
    },
);
